/**
 * OpenStatusBadge Component
 * Implements requirements 9.1, 9.2: Detailed opening status display with tooltips
 * 営業状況バッジ表示（営業中/営業時間外/不明系）
 * 不明系バッジにはツールチップ付与
 */

import React, { useState } from 'react';
import { OpenStatus } from '../types/core';

export interface OpenStatusBadgeProps {
    status: OpenStatus;
    className?: string;
}

// 営業状況の表示テキストとスタイル
const statusConfig = {
    open: {
        text: '営業中',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200',
        tooltip: null
    },
    closed: {
        text: '営業時間外',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200',
        tooltip: null
    },
    unknown_no_data: {
        text: '不明',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200',
        tooltip: '営業時間情報なし'
    },
    unknown_parse_failed: {
        text: '不明',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200',
        tooltip: '解析できず'
    },
    unknown_api_error: {
        text: '不明',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200',
        tooltip: '取得失敗'
    }
};

export const OpenStatusBadge: React.FC<OpenStatusBadgeProps> = ({
    status,
    className = ''
}) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const config = statusConfig[status] || statusConfig.unknown_no_data;

    const handleMouseEnter = () => {
        if (config.tooltip) {
            setShowTooltip(true);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    const handleFocus = () => {
        if (config.tooltip) {
            setShowTooltip(true);
        }
    };

    const handleBlur = () => {
        setShowTooltip(false);
    };

    return (
        <div className="relative inline-block">
            <span
                className={`
          inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border
          ${config.bgColor} ${config.textColor} ${config.borderColor}
          ${config.tooltip ? 'cursor-help' : ''}
          ${className}
        `}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleFocus}
                onBlur={handleBlur}
                tabIndex={config.tooltip ? 0 : -1}
                role={config.tooltip ? 'button' : undefined}
                aria-label={config.tooltip ? `${config.text} - ${config.tooltip}` : config.text}
            >
                {/* ステータスアイコン */}
                {status === 'open' && (
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                )}
                {status === 'closed' && (
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                )}
                {status.startsWith('unknown') && (
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                )}

                {config.text}
            </span>

            {/* ツールチップ */}
            {config.tooltip && showTooltip && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-10">
                    <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                        {config.tooltip}
                        {/* ツールチップの矢印 */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                            <div className="border-4 border-transparent border-t-gray-900"></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OpenStatusBadge;